import React, { ChangeEvent, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { InputBox } from "../../../../../../../components/balances/PayoutModal/style";
import { Input } from "../../../../../../../components/common/Input";
import { motion } from "framer-motion";
import { Form, ButtonWrapper } from "../style";
import { Button } from "../../../../../../../components/common/Button";
import {
  EXCLUDED_CURRENCIES_FOR_ID_DOC_PAYOUT,
  nextVariants,
} from "../../../../../utils";
import CustomSelect from "../../../../../../../components/common/CustomSelect";
import {
  getAllBankAccount,
  getBankAccountDetail,
  resolveZarAccount,
} from "../../../../../api";
import ErrorMessage from "../../../../../../../components/ErrorMessage";
import Loader from "../../../../../../../components/common/Loader";
import { NetworkErrorAlertContext } from "../../../../../../../context/NetworkErrorAlert";
import { useAppSelector } from "../../../../../../../app/hooks";
import { InputPhone } from "../../../../../../../components/common/InputPhone";
import { countryPhoneCodes, staticCurrencies, TZSPayouts } from "../../../../../const";
import {capitalizeCurrencyCode} from "../../../../../../../utils/helpers";

export type FormInputs = {
  bankName?: string;
  accountType?: string;
  accountName?: string;
  phone?: string;
  description?: string;
  accountNumber?: string;
  bankCode?: string;
  label?: string;
  value?: string;
  identityNumber?: string;
  account_name?: string;
  account_number?: string;
  zarAccountName?: string;
  zarAccountNumber?: string;
  identityType?: string;
  IdentityDoc?: string;
  phoneNumber?: string;
  recipientAddress?: string;
  bic?: string;
  serviceCode?: string;
  recipientEmail?: string;
};

type LocalTransferProps = {
  onNext: () => void;
  setlocalBankInfo: (data: FormInputs) => void;
  localBankInfo: FormInputs;
  setBankName?: (data: string) => void;
  setInitialAccountName?: (data: string) => void;
  initialAccountName?: string;
  identity?: string;
  setIdentity?: (data: string) => void;
  accountType?: string;
  setAccountType?: (data: string) => void;
};

type SelectedBankObject = {
  label: string;
  value: string;
  originalCode?: string;
};

const BankInfo = ({
  onNext,
  setlocalBankInfo,
  localBankInfo,
  setBankName,
  setInitialAccountName,
  initialAccountName,
  identity,
  setIdentity,
  accountType,
  setAccountType,
}: LocalTransferProps) => {
  const [formData, setFormData] = useState<FormInputs>(localBankInfo || {});
  const { currencyCode } = useParams();
  const [banks, setBanks] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("no-error");
  const { onShowAlert } = useContext(NetworkErrorAlertContext);
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      ...formData,
      accountName: initialAccountName,
    },
  });

  const watchAll = watch();

  const accountNumberValue: any = watch("accountNumber");
  const { businessId } = useAppSelector((state) => state.users || {});

  const updateIdentiTy = (data) => {
    if (data === "IdentityNumber") {
      return "identityNumber";
    }
    return data;
  };

  const accountTypes = [
    { value: "business", label: "Business" },
    { value: "personal", label: "Personal" },
  ];

  const identityOptions = [
    {
      value: "IdentityNumber",
      label: "Identity number",
    },
    {
      value: "passportNumber",
      label: "Passport number",
    },
  ];

  const selectBanks = useMemo(() => {
    const countryPayoutOptions = {
      TZS: TZSPayouts,
    };
    const currentWallet = {
      TZS: capitalizeCurrencyCode(currencyCode) === "TZS",
    };
    return currentWallet[capitalizeCurrencyCode(currencyCode)]
      ? countryPayoutOptions[capitalizeCurrencyCode(currencyCode)]
      : Array.isArray(banks)
      ? banks
          // Find out why National Bank Replaces all bank when in the list
          .filter((bank) => bank.name !== "National Bank" && bank.code !== "AIRTEL")
          .map((bank, index) => ({
            label: bank.name,
            value: `${bank.code}_${index}`,
            originalCode: bank.code,
          }))
      : [];
  }, [banks]);

  const bankLookupMap = useMemo(() => {
    return selectBanks.reduce((acc, bank) => {
      acc[bank.value] = bank;
      return acc;
    }, {});
  }, [selectBanks]);

  const [selectedBank, setSelectedBank] = useState<SelectedBankObject>();

  const handleBankChange = (selectedModifiedValue) => {
    const positionOfUnderscore = selectedModifiedValue.indexOf("_");
    const originalBankCode = capitalizeCurrencyCode(currencyCode) === "TZS" ? 
      selectedModifiedValue : 
      selectedModifiedValue.substring(0, positionOfUnderscore);

    const selectedBank = bankLookupMap[selectedModifiedValue];

    setValue("bankCode", originalBankCode);
    setBankName(selectedBank?.label);
    setSelectedBank(selectedBank);
    setlocalBankInfo({
      ...selectedBank,
      value: originalBankCode,
    });
  };

  const handleAccountType = (option) => {
    setAccountType(option);
  };

  const handleIdentityType = (data) => {
    setIdentity(data);
  };

  const removeLastCharacter = (arg: string) => {
    if (typeof arg === "string" && arg?.length > 0) {
      return arg.slice(0, -1);
    }
    return arg;
  };

  const accountLabel =
    accountType === "business"
      ? "Business registration number"
      : "Identity or passport number";

  const identityLabel =
    identity === "IdentityNumber"
      ? "Enter the identity number"
      : "Enter the passport number";

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        await getAllBankAccount({
          currency: capitalizeCurrencyCode(currencyCode),
        }).then((data) => {
          setBanks(data.data ? data.data : data);
          setLoading(false);
        });
      } catch (error) {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const modifiedCurrencyCode = removeLastCharacter(currencyCode);

        await getBankAccountDetail({
          accountNumber: accountNumberValue,
          bankCode: localBankInfo?.value,
          countryCode: capitalizeCurrencyCode(modifiedCurrencyCode),
        }).then((data) => {
          setlocalBankInfo(data);
          setInitialAccountName(data.account_name);
          setLoading(false);
          setError("no-error");
        });
      } catch (error) {
        setError("error");
        setLoading(false);
      }
    }

    if (localBankInfo?.value && accountNumberValue?.length === 10) {
      fetchData();
    }
  }, [accountNumberValue, localBankInfo?.value]);

  const resolveZARAccountNumber = async () => {
    setLoading(true);
    const ZarPayload = {
      accountNumber: watchAll.zarAccountNumber,
      accountName: watchAll.zarAccountName,
      bankCode: watchAll.bankName,
      accountType: accountType,
      documentNumber: watchAll.identityNumber || watchAll.identityType,
      countryCode: removeLastCharacter(capitalizeCurrencyCode(currencyCode)),
      documentType: updateIdentiTy(identity) || "businessRegistrationNumber",
      businessId: businessId,
    };

    const KESPayload = {
      country: removeLastCharacter(capitalizeCurrencyCode(currencyCode)),
      bankCode: watchAll.bankName,
      accountName: watchAll.zarAccountName,
      accountNumber: watchAll.zarAccountNumber,
      currency: capitalizeCurrencyCode(currencyCode),
      bankName: watchAll.bankName,
      description: watchAll.description,
      type: accountType,
    };
    const selectedPayload =
        currencyCode === "zar" ? ZarPayload : currencyCode === "kes" ? KESPayload : null

    await resolveZarAccount(selectedPayload)
      .then((res) => {
        setLoading(false);
        onNext();
      })
      .catch((errr) => {
        onShowAlert("", errr?.response?.data?.error);
        setLoading(false);
      });
  };

  const handleUpdatePayment = () => {
    if (currencyCode === "zar") {
      resolveZARAccountNumber();
    } else {
      onNext();
    }
  };

  const isTzsWithMomo = currencyCode === "tzs" && 
    localBankInfo?.bankCode && 
    localBankInfo.bankCode !== "TZ-BANK-B2C" &&
    TZSPayouts.some(item => item.value === localBankInfo.bankCode);

  return (
    <motion.div
      variants={nextVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <Loader isLoading={loading} />
      <Form>
        <InputBox>
          <Controller
            name="bankName"
            control={control}
            defaultValue={currencyCode === "tzs" ? localBankInfo?.bankCode : null}
            rules={{
              required: capitalizeCurrencyCode(currencyCode) === "GHS" ? "Please select the account type" : "Please input the beneficiary's Bank name",
            }}
            render={({ field }) => (
              <CustomSelect
                placeholder={`${
                  capitalizeCurrencyCode(currencyCode) === "GHS"
                    ? "Account type"
                    : `${
                        capitalizeCurrencyCode(currencyCode) === "KES"
                          ? "Select bank/Wallet type"
                          : "Select bank"
                      }`
                }`}
                $width="100%"
                field={field}
                disableLabel
                options={selectBanks}
                onChange={handleBankChange}
                showSearch
                filterOption={(inputValue, option) =>
                  option.label.toLowerCase().includes(inputValue.toLowerCase())
                }
                lightBorder={true}
                disabled={currencyCode === "tzs"}
              />
            )}
          />
          <span className={"error"}>
            {errors.bankName?.message}
          </span>
        </InputBox>

        {capitalizeCurrencyCode(currencyCode) === "KES" &&
          !selectedBank?.label.includes("MOBILE MONEY") &&
          selectedBank?.value.includes("M-PESA") && (
            <div>
              <InputBox>
                <Controller
                  name="serviceCode"
                  control={control}
                  rules={{
                    required: "Please enter the beneficiary's bank short code",
                  }}
                  render={({ field }) => (
                    <Input
                      type="text"
                      label="Bank/Service Short Code"
                      field={field}
                      labelColor="#FFFFFF"
                      disableLabel
                    />
                  )}
                />
                <span className={"error"}>
                  {errors.serviceCode?.message}

                </span>

              </InputBox>
            </div>
          )}

        {capitalizeCurrencyCode(currencyCode) === "GHS" && (
          <div>
            <InputBox>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Please enter the beneficiary's phone number",
                  validate: (value) =>
                    value.length === 10 || "Phone number must be 10 digits",
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    label="Phone number"
                    field={field}
                    labelColor="#FFFFFF"
                    disableLabel
                  />
                )}
              />
              <span className={"error"}>
                {errors.phone?.message}

              </span>

            </InputBox>
          </div>
        )}

        {localBankInfo?.bankCode === "TZ-BANK-B2C" && (
          <div>
            <InputBox>
              <Controller
                name="bic"
                control={control}
                rules={{
                  required:
                    "Please enter the beneficiary's bank Business Identifier Code",
                }}
                render={({ field }) => (
                  <Input
                    type="text"
                    label="BIC"
                    field={field}
                    labelColor="#FFFFFF"
                    disableLabel
                  />
                )}
              />
              <span className={"error"}>
                {errors.bic?.message}

              </span>

            </InputBox>
          </div>
        )}

        {currencyCode === "ngn" && (
          <div>
            <InputBox>
              <Controller
                name="accountNumber"
                control={control}
                rules={{
                  required: "Please input the beneficiary's account number",
                  pattern: /^\d*$/,
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    label="Account Number"
                    field={field}
                    labelColor="#FFFFFF"
                    disableLabel
                  />
                )}
              />
              {watchAll?.accountNumber &&
                (watchAll?.accountNumber?.length !== 10 &&
                currencyCode === "ngn" &&
                watchAll?.accountNumber?.length !== 0 ? (
                  <ErrorMessage message="Account number must be 10 digits" />
                ) : (
                  error === "error" && (
                    <ErrorMessage message="Account number is not valid" />
                  )
                ))}

              <span className={"error"}>
                {errors.accountNumber?.message}
              </span>

            </InputBox>

            <InputBox>
              <Controller
                name="accountName"
                control={control}
                rules={{
                  required: (currencyCode !== "ngn") && "Please input the beneficiary's account name",
                }}
                render={({ field }) => (
                  <Input
                    type="text"
                    label="Account Name"
                    value={initialAccountName}
                    labelColor="#FFFFFF"
                    field={field}
                    disableLabel
                    disabled={currencyCode === "ngn"}
                  />
                )}
              />
              <span className={"error"}>
                {errors.accountName?.message}

              </span>

            </InputBox>
          </div>
        )}

        {staticCurrencies.includes(currencyCode) && (
          <div>
            <InputBox>
              <Controller
                name="zarAccountNumber"
                control={control}
                rules={{
                  required: "Please enter the beneficiary's account number"
                }}
                render={({ field: { onChange, value, ...field } }) => (
                  <Input
                    type="number"
                    label={`${
                      capitalizeCurrencyCode(currencyCode) === "KES"
                        ? "Account /Momo number"
                        : isTzsWithMomo  ? "Phone number (Momo)" : "Account number"
                    }`}
                    labelColor="#FFFFFF"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      let newValue = e.target.value;
                      if (isTzsWithMomo && newValue.startsWith('0')) {
                        newValue = newValue.substring(1);
                      }
                      onChange(newValue);
                    }}
                    value={value}
                    {...field}
                    disableLabel
                    showCountryPhoneCode={isTzsWithMomo}
                    paddingForPrefix={isTzsWithMomo && "4.5rem"}
                    countryPhoneCode={isTzsWithMomo && countryPhoneCodes[capitalizeCurrencyCode(currencyCode)]}
                  />
                )}
              />
              <span className={"error"}>
                {errors.zarAccountNumber?.message}

              </span>

            </InputBox>

            <InputBox>
              <Controller
                name="zarAccountName"
                control={control}
                rules={{
                  required: "Please enter the beneficiary's account name",
                }}
                render={({ field }) => (
                  <Input
                    type="text"
                    label="Account name"
                    labelColor="#FFFFFF"
                    field={field}
                    disableLabel
                  />
                )}
              />
              <span className={"error"}>
                {errors.zarAccountName?.message}
              </span>

            </InputBox>

            {currencyCode !== "tzs" && (
              <InputBox>
                <Controller
                  name="accountType"
                  control={control}
                  rules={{
                    required: "The account type is required",
                  }}
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="Account Type"
                      $width="100%"
                      field={field}
                      disableLabel
                      options={
                       currencyCode === "kes" || currencyCode === "zwm"
                          ? accountTypes.slice(1)
                          : accountTypes.slice(0)
                      }
                      onChange={handleAccountType}
                      lightBorder={true}
                    />
                  )}
                />
                <span className={"error"}>
                  {errors.accountType?.message}

                </span>
              </InputBox>
            )}

            {(accountType === "personal" && currencyCode !== "kes") ||
              (!EXCLUDED_CURRENCIES_FOR_ID_DOC_PAYOUT.includes(
                currencyCode,
              ) && (
                <InputBox>
                  <Controller
                    name="IdentityDoc"
                    control={control}
                    rules={{
                      required: "The document type is required",
                    }}
                    render={({ field }) => (
                      <CustomSelect
                        placeholder="Select document type"
                        $width="100%"
                        field={field}
                        disableLabel
                        options={identityOptions}
                        onChange={handleIdentityType}
                        lightBorder={true}
                      />
                    )}
                  />
                  <span className={"error"}>
                    {errors.accountType?.message}

                  </span>
                </InputBox>
              ))}

            {accountType === "business" && (
              <InputBox>
                <Controller
                  name="identityNumber"
                  control={control}
                  rules={{
                    required: `Please enter the ${accountLabel}`,
                  }}
                  render={({ field }) => (
                    <Input
                      type="text"
                      label="Business registration number"
                      value={initialAccountName}
                      labelColor="#FFFFFF"
                      field={field}
                      disableLabel
                    />
                  )}
                />
                <span className={"error"}>
                  {errors.identityNumber?.message}

                </span>

              </InputBox>
            )}
          </div>
        )}

        {currencyCode === "zar" && (
          <React.Fragment>
          <InputBox>
            <Controller
              name="recipientAddress"
              control={control}
              rules={{
                required: "Please enter the Recipient Address",
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Recipient address"
                  labelColor="#FFFFFF"
                  field={field}
                  disableLabel
                />
              )}
            />
            <span className={"error"}>
              {errors.recipientAddress?.message}

            </span>

          </InputBox>

          <InputBox>
            <Controller
              name="recipientEmail"
              control={control}
              rules={{
                required: "Please enter the Recipient Email Address",
                pattern: emailRegex,
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  label="Recipient email address"
                  labelColor="#FFFFFF"
                  field={field}
                  disableLabel
                />
              )}
            />

            <span className={"error"}>
              {errors.recipientEmail?.message}
            </span>
            {errors.recipientEmail?.type === "pattern" && (
              <span className={"error"}>Invalid email format</span>
            )}
          </InputBox>

          <InputBox>
            <Controller
              name="phoneNumber"
              rules={{
                required: "Phone number is required",
              }}
              control={control}
              render={({ field }) => (
                <InputPhone
                  label="Phone number"
                  field={field}
                  hasError={errors?.phoneNumber?.type === "required"}
                  defaultCountry="ZA"
                />
              )}
            />
            <span className={"error"}>{errors?.phoneNumber?.message}</span>
          </InputBox>
          </React.Fragment>
        )}

        {currencyCode === "zar" && identity && accountType !== "business" && (
          <InputBox>
            <Controller
              name="identityType"
              control={control}
              rules={{
                required: "The field is required",
              }}
              render={({ field }) => (
                <Input
                  type="text"
                  label={identityLabel}
                  field={field}
                  labelColor="#FFFFFF"
                  disableLabel
                />
              )}
            />
            <span className={"error"}>
              {errors.identityType?.message}

            </span>
          </InputBox>
        )}

        <InputBox>
          <Controller
            name="description"
            control={control}
            rules={{
              required: false,
            }}
            render={({ field }) => (
              <Input
                type="text"
                label="Payment description (optional)"
                field={field}
                labelColor="#FFFFFF"
                disableLabel
              />
            )}
          />
        </InputBox>
        <ButtonWrapper>
          <p>Funds should arrive within 1-3 working days.</p>
          <Button
            label="Continue"
            theme="primary"
            type="submit"
            height="48px"
            fontSize="14px"
            disabled={error === "error"}
            onClick={handleSubmit((data) => {
              setlocalBankInfo(data);
              setFormData(data);
              handleUpdatePayment();
            })}
          />
        </ButtonWrapper>
      </Form>
    </motion.div>
  );
};

export default BankInfo;
